import httpStatus from 'http-status';
import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Col, Form } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { DEFAULT_COUNTRY_CODE } from '@constants/constants';
import { appInsights } from '@/telemetry/client';
import { CheckboxField } from '@components/Form/CheckboxField';
import { TextField } from '@components/Form/TextField';
import { cocNumberLookup, vatNumberLookup } from '@services/vat-client';
import { DeliveryAddress } from './DeliveryAddress';
import { Address } from './Address';

type AddressReadOnlyState = {
  companyName: boolean;
  street: boolean;
  houseNumber: boolean;
  houseNumberAddition: boolean;
  postalCode: boolean;
  city: boolean;
};

const DEFAULT_READONLY_STATE: AddressReadOnlyState = {
  companyName: false,
  street: false,
  houseNumber: false,
  houseNumberAddition: false,
  postalCode: false,
  city: false
};

export const BusinessInformation = ({ countryData }: { countryData: any[] }) => {
  const { t } = useTranslation('register');
  const formikContext = useFormikContext<any>();
  const countryCode = formikContext.values.countryCode;

  const [showChamberOfCommerceInput, setShowChamberOfCommerceInput] = useState(countryCode === DEFAULT_COUNTRY_CODE);
  const [addressReadOnly, setAddressReadOnly] = useState<AddressReadOnlyState>(DEFAULT_READONLY_STATE);

  useEffect(() => {
    setShowChamberOfCommerceInput(countryCode === DEFAULT_COUNTRY_CODE);
  }, [countryCode]);

  const doVATLookup = async (event: React.FormEvent<HTMLInputElement>) => {
    const { vatRegistrationNr: _, ...remainingStatus } = formikContext.status || {};
    formikContext.setStatus(remainingStatus);

    const vatRegistrationNr = event.currentTarget.value;

    if (vatRegistrationNr.length < 4) {
      return;
    }

    try {
      const response = await vatNumberLookup({ vatRegistrationNr });

      if (!response.valid) {
        formikContext.setStatus({ vatRegistrationNr: 'form-errors:invalid.vat-number' });
        return;
      }

      const { companyName, address } = response;
      const country = countryData.find((country) => country.code === address.countryCode?.toLocaleUpperCase());

      formikContext.setFieldValue('vatRegistrationNr', vatRegistrationNr.toLocaleUpperCase());
      formikContext.setFieldValue('countryCode', country?.code ?? DEFAULT_COUNTRY_CODE, false);
      formikContext.setFieldValue('companyName', companyName, false);
      formikContext.setFieldValue('postalCode', address?.postalCode, false);
      formikContext.setFieldValue('houseNumber', address.houseNumber, false);
      formikContext.setFieldValue('houseNumberAddition', address.houseNumberAddition, false);
      formikContext.setFieldValue('street', address.street, false);
      formikContext.setFieldValue('city', address.city, false);
      formikContext.setFieldValue('manualAddress', true, false);
    } catch (error: any) {
      const statusCode = error?.response?.status;

      if (statusCode === httpStatus.CONFLICT) {
        formikContext.setStatus({ vatRegistrationNr: 'form-errors:invalid.vat-number' });
      } else if (statusCode === httpStatus.SERVICE_UNAVAILABLE) {
        formikContext.setStatus({ vatRegistrationNr: 'form-errors:unavailable.vat-number' });
      }
      appInsights.trackException(
        {
          exception: error instanceof Error ? error : new Error('VAT lookup failed')
        },
        {
          statusCode: statusCode?.toString() ?? '',
          url: error?.config?.url ?? ''
        }
      );
    }
  };

  const doCocLookup = async (event: React.FormEvent<HTMLInputElement>) => {
    const { registrationNr: _, ...remainingStatus } = formikContext.status || {};
    formikContext.setStatus(remainingStatus);

    const registrationNr = event.currentTarget.value;

    // Reset to default state
    setAddressReadOnly(DEFAULT_READONLY_STATE);

    const isValidCocNumber = /^[0-9]{8}$/.test(registrationNr);
    if (!isValidCocNumber) {
      return;
    }

    try {
      const companyData = await cocNumberLookup({ registrationNr });

      if (!companyData) {
        formikContext.setStatus({ registrationNr: 'form-errors:matches.coc-number' });
        return;
      }

      // Set form values and track which fields are populated
      const readOnlyState: AddressReadOnlyState = { ...DEFAULT_READONLY_STATE };

      formikContext.setFieldValue('countryCode', DEFAULT_COUNTRY_CODE);

      const fieldsToCheck: (keyof typeof companyData)[] = ['companyName', 'street', 'houseNumber', 'postalCode', 'city'];

      fieldsToCheck.forEach((field) => {
        const fieldValue = companyData[field];
        // Only set readonly if field has a meaningful value (not null, undefined, or empty string)
        if (fieldValue && fieldValue.toString().trim() !== '') {
          formikContext.setFieldValue(field, fieldValue, false);
          readOnlyState[field as keyof AddressReadOnlyState] = true;
        }
      });

      setAddressReadOnly(readOnlyState);
    } catch (error) {
      appInsights.trackException({ exception: new Error(error as any) });
      setAddressReadOnly(DEFAULT_READONLY_STATE);
    }
  };

  return (
    <>
      {showChamberOfCommerceInput && (
        <Form.Group>
          <Form.Row>
            <Form.Group as={Col}>
              <h2 className="h2">{t('title.chamber-of-commerce-and-vat-number')}</h2>
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col} xs={12} md={6}>
              <TextField label={t('label.coc-number')} type="text" name="registrationNr" onBlur={doCocLookup} />
            </Form.Group>
          </Form.Row>
        </Form.Group>
      )}
      {!showChamberOfCommerceInput && (
        <Form.Group>
          <Form.Row>
            <Form.Group as={Col}>
              <h2 className="h2">{t('title.vat-number')}</h2>
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col} xs={12}>
              <TextField label={t('label.vat-number')} type="text" name="vatRegistrationNr" onBlur={doVATLookup} />
            </Form.Group>
          </Form.Row>
        </Form.Group>
      )}
      <Form.Group>
        <Form.Row>
          <Form.Group as={Col}>
            <h2>{t('title.business-information')}</h2>
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col} xs={12}>
            <TextField label={t('label.company-name')} type="text" name="companyName" readOnly={addressReadOnly.companyName} />
          </Form.Group>
        </Form.Row>
      </Form.Group>

      <Address readOnly={addressReadOnly} />

      <Form.Group>
        <Form.Row>
          <Form.Group as={Col}>
            <Form.Label>{t('title.different-shipping-address')}</Form.Label>
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col}>
            <CheckboxField label={t('label.different-shipping-address')} name="hasDeliveryAddress" custom shouldValidate={false} />
          </Form.Group>
        </Form.Row>
        {formikContext.values.hasDeliveryAddress && <DeliveryAddress />}
      </Form.Group>
    </>
  );
};
